import type { Territory, TerritoryCode, TerritoryRegion } from '../territories';

/**
 * This function takes a record of territories and groups them by their region.
 * It returns an object where the keys are region names and the values are
 * arrays of Territories.
 *
 * @param territories - A record of territories where the key is the territory code.
 * @returns An object keyed on region name and that has an array of Territory items as data.
 **/
export function getTerritoriesByRegion(
  territories: Record<TerritoryCode, Territory>,
): TerritoryRegion {
  const regions: TerritoryRegion = {};

  // Iterate through the territories and group them by region.
  Object.entries(territories).forEach(([, data]) => {
    const region = data.region;

    if (!regions[region]) {
      regions[region] = [];
    }

    regions[region].push(data);
  });

  // Sort the regions alphabetically into an array.
  const regionsArray = Object.entries(regions)
    .map(([region, data]) => ({ region, data }))
    .sort((r1, r2) => r1.region.localeCompare(r2.region));

  // Convert the sorted array back to an object.
  const sortedRegions: TerritoryRegion = {};

  regionsArray.forEach(({ region, data }) => {
    sortedRegions[region] = data;
  });

  return sortedRegions;
}
